import { Button, CharField, Flex, H1, Tabs, useBreadcrumbs } from "@inmagik/magik-ui"
import Layout from "../../components/Layout"
import { useNavigate, useParams } from "react-router-dom"
import { Formik } from "formik"
import { Container } from "react-bootstrap"
import BottomFormFixed from "../../components/BottomFormFixed"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useMemo, useState } from "react"
import ModalDelete from "../../components/ModalDelete"
import { ScrollLayout } from "../../components/ScrollLayout"
import type { Document, FiltersBase } from "../../types"
import {
  useCreateMediaDocument,
  useDeleteDocument,
  useDocument,
  useMediaListDocument,
  useUpdateDocument,
} from "../../hooks/documents"
import { initialValuesDocument, validationSchemaDocument } from "./DocumentCommon"
import DocumentForm from "./DocumentForm"
import { TableMedia } from "./TableMedia"
import { useQsFilters } from "../../hooks/filters"
import MediaDocumentModal from "./MediaDocumentModal"

const initFilters = (params: URLSearchParams) =>
  ({
    page: Number(params.get("page") ?? 1),
    search: params.get("search") ?? "",
    ordering: params.get("ordering") ?? "",
  } as FiltersBase)

export default function DocumentUpdate() {
  const updateDocument = useUpdateDocument()
  const { filters, uiFilters, setFilters } = useQsFilters(initFilters)
  const navigate = useNavigate()

  const { id } = useParams()

  const { data: document } = useDocument(Number(id))

  const { data: mediaList, refetch } = useMediaListDocument(Number(id))

  const deleteDocument = useDeleteDocument()

  const [modalStateDeleteDocument, modalActionsDeleteDocument] = useModalTrigger()
  const [modalImmagineCreateState, modalImmagineCreateActions] = useModalTrigger()

  const [activeTab, setActiveTab] = useState("dettaglio")

  const initialValues = useMemo(() => initialValuesDocument(document), [document])
  const validationSchema = useMemo(() => validationSchemaDocument(), [])

  const createMediaDocument = useCreateMediaDocument(Number(id))

  useBreadcrumbs([{ label: "Documenti", href: "/documents/documents" }, { label: "Dettaglio Documento" }])

  return (
    <Layout>
      <ScrollLayout hasBottomFixed>
        <Flex alignItems="center" justifyContent="between">
          <H1>{document?.title}</H1>
          {activeTab === "dettaglio" && (
            <Button
              size="md"
              title="Elimina"
              variant="danger"
              leadingIcon="delete"
              onClick={() => {
                modalActionsDeleteDocument.open()
              }}
            />
          )}
          {activeTab === "immagini" && (
            <Button
              size="md"
              className="text-white"
              title="Aggiungi"
              leadingIcon="add"
              onClick={() => {
                modalImmagineCreateActions.open({
                  order: mediaList && mediaList.length > 0 ? mediaList[mediaList.length - 1].order + 1 : 1,
                })
              }}
            />
          )}
        </Flex>
        <ScrollLayout.Tabs
          className="mt-6"
          onTabChange={(tab) => {
            setActiveTab(tab)
          }}
          initialTab="dettaglio"
        >
          <ScrollLayout.Tab id="dettaglio" label="Dettaglio" key={"dettaglio"}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                return updateDocument.mutateAsync(values as Document).then(() => {
                  navigate(`/documents/documents`)
                })
              }}
            >
              {({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <Container fluid className="mt-6" style={{ marginBottom: "100px" }}>
                    <DocumentForm />
                  </Container>
                  <BottomFormFixed url="/documents/documents" />
                </form>
              )}
            </Formik>
          </ScrollLayout.Tab>
          <Tabs.Tab id="immagini" label="Immagini" key={"immagini"}>
            <Flex className="mt-3">
              <CharField
                label=""
                placeholder="Cerca..."
                leadingIcon="search"
                style={{
                  width: 300,
                }}
                value={filters.search}
                onChange={(value) => {
                  setFilters({ ...filters, search: value, page: 1 })
                }}
              />
            </Flex>
            <div className="mt-3">
              <TableMedia recordMedia={mediaList || []} setFilters={setFilters} filters={uiFilters} refetch={refetch} />
            </div>
          </Tabs.Tab>
        </ScrollLayout.Tabs>
      </ScrollLayout>
      <ModalDelete
        isOpen={modalStateDeleteDocument.isOpen}
        onClose={modalActionsDeleteDocument.close}
        onDelete={() => {
          return deleteDocument.mutateAsync(document?.id).then(() => {
            navigate(`/documents/documents`)
          })
        }}
        message="Sei sicuro di voler eliminare il documento?"
        title="Elimina Documento"
      />
      {modalImmagineCreateState.isOpen && (
        <MediaDocumentModal
          title="Aggiungi Immagine"
          id={document?.id}
          order={modalImmagineCreateState.value.order}
          show={modalImmagineCreateState.isOpen}
          onClose={modalImmagineCreateActions.close}
          save={async (values) => {
            const result = await createMediaDocument.mutateAsync(values)
            if (result) {
              modalImmagineCreateActions.close()
              refetch()
            }
          }}
        />
      )}
    </Layout>
  )
}
